import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LangContext';

export default function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const { t } = useLang();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!email) {
      toast.error(t('alerts.required_fields'));
      return;
    }
    setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
      toast.success(t('auth.reset_request_success'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('alerts.generic_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg">
        <Card className="rounded-2xl shadow-sm">
          <CardContent className="p-8 sm:p-10">
            <div className="h-12 w-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-6">
              <Mail className="h-6 w-6" />
            </div>
            <h1 className="text-2xl font-semibold mb-2">{t('auth.forgot_password')}</h1>
            <p className="text-sm text-muted-foreground">
              {sent ? t('auth.reset_request_sent_desc') : t('auth.reset_request_desc')}
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <Label className="text-sm font-medium mb-1.5">{t('auth.email')}</Label>
                <Input
                  type="email"
                  className="rounded-lg"
                  placeholder={t('auth.email')}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              <Button type="submit" className="w-full rounded-xl h-11" disabled={loading}>
                {loading ? t('auth.sending_reset') : t('auth.send_reset_link')}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              <Link to="/login" className="text-primary font-medium hover:underline">
                {t('verify.back_login')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
